import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { AMOUNT_DOLLARS_MAX, EditableEntryField, FACILITY_NAME_MAX, PROCEDURE_CODE_MAX, COMMENT_MAX, MIN_SERVICE_DATE } from '../editable-field-config'

dayjs.extend(customParseFormat)

export const DEFAULT_VALIDATION_ERROR = 'Invalid value'

/**
 * Validates a field value based on the field key
 * Returns error message if invalid, null if valid
 */
export function validateField(fieldKey: EditableEntryField, value: string): string | null {
  switch (fieldKey) {
    case EditableEntryField.FACILITY_NAME:
      return validateFacilityName(value)
    case EditableEntryField.PROCEDURE_CODE:
      return validateProcedureCode(value)
    case EditableEntryField.AMOUNT:
      return validateAmount(value)
    case EditableEntryField.SERVICE_DATE:
      return validateServiceDate(value)
    case EditableEntryField.COMMENT:
      return validateComment(value)
    default:
      return null
  }
}

/**
 * Validate facility name field
 */
export function validateFacilityName(value: string): string | null {
  if (!value || value.trim().length === 0) {
    return 'Facility cannot be empty'
  }

  // This is enforced by maxLength in EditFieldPopover, but checking here for extra safety
  if (value.trim().length > FACILITY_NAME_MAX) {
    return `Facility must be ${FACILITY_NAME_MAX} characters or less`
  }
  return null
}

/**
 * Validate procedure code field
 */
export function validateProcedureCode(value: string): string | null {
  // Procedure code is nullable - empty is valid (user can clear the field)
  if (!value || value.trim().length === 0) {
    return null
  }

  // This is enforced by maxLength in EditFieldPopover, but checking here for extra safety
  if (value.trim().length > PROCEDURE_CODE_MAX) {
    return `Procedure code must be ${PROCEDURE_CODE_MAX} characters or less`
  }
  return null
}

/**
 * Validate comment field
 */
export function validateComment(value: string): string | null {
  // Comment is nullable - empty is valid (user can clear the field)
  if (!value || value.trim().length === 0) {
    return null
  }

  // This is enforced by maxLength in EditFieldPopover, but checking here for extra safety
  if (value.trim().length > COMMENT_MAX) {
    return `Comment must be ${COMMENT_MAX} characters or less`
  }
  return null
}

/**
 * Validate amount field
 */
export function validateAmount(value: string): string | null {
  if (!value || value.trim().length === 0) {
    return DEFAULT_VALIDATION_ERROR
  }

  // Check for valid numeric format (should already be enforced by the mask)
  const amountRegex = /^\d+(\.\d{0,2})?$/
  if (!amountRegex.test(value)) {
    return DEFAULT_VALIDATION_ERROR
  }

  // Check amount does not exceed maximum dollar amount
  const numericValue = parseFloat(value)
  if (numericValue > AMOUNT_DOLLARS_MAX) {
    return DEFAULT_VALIDATION_ERROR
  }

  return null
}

/**
 * Validate service date field
 */
export function validateServiceDate(value: string): string | null {
  if (!value || value.trim().length === 0) {
    return DEFAULT_VALIDATION_ERROR
  }

  // Parse and validate the date is real (e.g., not 02/31/2024)
  // Strict mode (third param = true) ensures exact format match
  const parsedDate = dayjs(value, 'MM/DD/YYYY', true)
  if (!parsedDate.isValid()) {
    return DEFAULT_VALIDATION_ERROR
  }

  // the date should be after Jan 1, 1950 and not in the future
  if (parsedDate.isBefore(dayjs(MIN_SERVICE_DATE), 'day') || parsedDate.isAfter(dayjs(), 'day')) {
    return DEFAULT_VALIDATION_ERROR
  }

  return null
}
